/**
 * The People tab's shared control state, published on `window.peopleControls`.
 *
 * Why a global pub/sub rather than React context: the People cluster spans a
 * React dashboard AND a jQuery/DataTables Positions table, and the two are
 * mounted into sibling tabpanels by Blade. The table cannot consume a React
 * context, so the rail publishes here and the table subscribes.
 *
 * Deliberately tiny — three methods, no dependencies, no framework:
 *
 *   window.peopleControls.getState()      → the current control state
 *   window.peopleControls.subscribe(fn)   → calls fn(state) on every change,
 *                                           returns an unsubscribe function
 *   window.peopleControls.setState(next)  → internal; the rail owns writes
 *
 * The store is created when this module is evaluated (i.e. when the app.js
 * bundle runs). A consumer that runs first can wait for the
 * `people-controls:ready` event instead — see `whenPeopleControlsReady` in
 * `resources/js/scripts/positionsTable.js`.
 */

export const PEOPLE_CONTROLS_READY_EVENT = 'people-controls:ready';

export const COUNT_MODE_HEADCOUNT = 'headcount';
export const COUNT_MODE_FTE = 'fte';

function createPeopleControlStore(initialState) {
    let state = initialState;
    const listeners = new Set();

    return {
        getState() {
            return state;
        },

        subscribe(listener) {
            listeners.add(listener);

            return () => listeners.delete(listener);
        },

        setState(next) {
            state = next;

            listeners.forEach((listener) => {
                // One broken subscriber must not stop the others: the dashboard
                // and the Positions table are independent consumers.
                try {
                    listener(state);
                } catch (error) {
                    console.error('[peopleControls] subscriber threw', error);
                }
            });
        },
    };
}

/**
 * Install the store on `window` once, seeded from the server-resolved
 * preference. Re-entrant: a second call returns the existing store so that
 * mounting the rail on a second Blade view never replaces live subscriptions.
 */
export default function installPeopleControlStore(initialState) {
    if (!window.peopleControls) {
        window.peopleControls = createPeopleControlStore(initialState);
        document.dispatchEvent(new CustomEvent(PEOPLE_CONTROLS_READY_EVENT));
    }

    return window.peopleControls;
}
